import { plainText } from '../plain-text'
import { useEffect, useRef } from 'react'
import { useMeasuredPages } from '../hooks/useMeasuredPages'

type PagedTextProps = {
  text?: string | null
  className?: string
  textClassName?: string
  resetKey: string
  label: string
}

export function PagedText(input: PagedTextProps) {
  const props = { ...input, text: plainText(input.text) }
  return <MeasuredText key={JSON.stringify([props.resetKey, props.text])} {...props} />
}

function MeasuredText({ text = '', className = '', textClassName = '', resetKey, label }: PagedTextProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const pageLabelRef = useRef<HTMLSpanElement | null>(null)
  const pagination = useMeasuredPages({ text: text || '', viewportRef, measureRef, resetKey })
  const { pages, pageIndex, setPageIndex, page, isFirst, isLast } = pagination
  const lastPageIndexRef = useRef(pageIndex)

  useEffect(() => {
    if (lastPageIndexRef.current !== pageIndex && pages.length > 1) pageLabelRef.current?.focus()
    lastPageIndexRef.current = pageIndex
  }, [pageIndex, pages.length])

  const movePage = (next: number) => setPageIndex(Math.min(Math.max(next, 0), pages.length - 1))

  return (
    <div className={`paged-text ${className}`.trim()} data-page-index={pageIndex} data-page-count={pages.length} data-source-text={text}>
      <div ref={viewportRef} className="paged-text-viewport" aria-live="polite" aria-label={`${label}正文`}>
        <span className={`paged-text-page ${textClassName}`.trim()} tabIndex={-1} data-text-start={page.start} data-text-end={page.end} data-page-index={pageIndex} data-page-count={pages.length} aria-label={`${label}第 ${pageIndex + 1} 页`}>{page.text}</span>
      </div>
      {pages.length > 1 && (
        <div className="paged-text-pager" aria-label={`${label}分页控件`}>
          <button type="button" className="text-button paged-text-control" aria-label={`${label}：上一页`} onClick={() => movePage(pageIndex - 1)} disabled={isFirst}>上一页</button>
          <span ref={pageLabelRef} className="paged-page-label" tabIndex={-1}>正文 {pageIndex + 1}/{pages.length}</span>
          <button type="button" className="text-button paged-text-control" aria-label={`${label}：下一页`} onClick={() => movePage(pageIndex + 1)} disabled={isLast}>下一页</button>
        </div>
      )}
      <div ref={measureRef} className={`paged-text-measure ${textClassName}`.trim()} aria-hidden="true" />
    </div>
  )
}
